import { useEffect, useState } from "react";
import axios from "axios";
import { Phone, Mail, MessageSquare, Calendar, X, CheckCircle2, Circle, Trash2, Users as UsersIcon, ArrowRight, Sparkles, MapPin, Globe, RefreshCw, PhoneCall, Pencil, Save, Eye, ExternalLink, Copy, CalendarPlus, Activity, Flame, Heart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import LostReasonModal from "@/components/sales/LostReasonModal";
import SendEmailModal from "@/components/sales/SendEmailModal";
import ScoreBreakdownPanel from "@/components/sales/ScoreBreakdownPanel";
import CallTrackerDialog from "@/components/sales/CallTrackerDialog";
import AppointmentDialog from "@/components/sales/AppointmentDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ACTIVITY_TYPES = {
  call: { icon: Phone, label: "Anruf", color: "text-blue-600" },
  note: { icon: MessageSquare, label: "Notiz", color: "text-slate-600" },
  email: { icon: Mail, label: "E-Mail", color: "text-violet-600" },
  meeting: { icon: Calendar, label: "Termin", color: "text-amber-600" },
  stage_change: { icon: ArrowRight, label: "Stage", color: "text-emerald-600" },
  assign: { icon: UsersIcon, label: "Owner", color: "text-cyan-600" },
  system: { icon: Circle, label: "System", color: "text-slate-400" },
};

// Deutscher Relativ-Timestamp aus ISO-String (für Tracking-Timeline)
function formatRelativeIso(iso) {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 0) return "gleich";
  const m = Math.floor(ms / 60000);
  if (m < 1) return "gerade eben";
  if (m < 60) return `vor ${m} Min.`;
  const h = Math.floor(m / 60);
  if (h < 24) return `vor ${h} Std.`;
  const d = Math.floor(h / 24);
  if (d < 7) return `vor ${d} Tag${d === 1 ? "" : "en"}`;
  const w = Math.floor(d / 7);
  return `vor ${w} Wo.`;
}

// Bekommt Minuten-Wert aus dem Backend (minutes_since_last_view) und macht daraus
// einen kompakten deutschen Label-Text für die KPI-Card.
function formatRelativeMinutes(m) {
  if (m === null || m === undefined) return "—";
  if (m < 1) return "gerade eben";
  if (m < 60) return `vor ${m} Min.`;
  const h = Math.floor(m / 60);
  if (h < 24) return `vor ${h} Std.`;
  const d = Math.floor(h / 24);
  if (d < 7) return `vor ${d} Tag${d === 1 ? "" : "en"}`;
  const w = Math.floor(d / 7);
  return `vor ${w} Wo.`;
}

const CALL_OUTCOMES = [
  { value: "erreicht", label: "Erreicht" },
  { value: "nicht_erreicht", label: "Nicht erreicht" },
  { value: "voicemail", label: "Mailbox" },
  { value: "kein_interesse", label: "Kein Interesse" },
  { value: "rueckruf", label: "Rückruf vereinbart" },
];

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("de-DE", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
}

export default function LeadDetailModal({ leadId, open, onClose, onUpdate, stages, users, currentUser }) {
  const [lead, setLead] = useState(null);
  const [activities, setActivities] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [tab, setTab] = useState("timeline");
  const [actType, setActType] = useState("call");
  const [actForm, setActForm] = useState({ title: "", body: "", outcome: "erreicht", duration_min: 0 });
  const [taskForm, setTaskForm] = useState({ title: "", type: "call", due_at: "", notes: "" });
  const [saving, setSaving] = useState(false);
  const [pendingLostStage, setPendingLostStage] = useState(null);
  const [showSendEmail, setShowSendEmail] = useState(false);
  const [showCallTracker, setShowCallTracker] = useState(false);
  const [showAppointment, setShowAppointment] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);
  const [editingTags, setEditingTags] = useState(false);
  const [tagInput, setTagInput] = useState("");
  const [allTags, setAllTags] = useState([]);
  // Pre-Build-Modal (öffnet vor convert-to-demo): Vertriebler kann 4 Felder
  // ergänzen die als „heilige Wahrheit" beim Demo-Build greifen, oder einfach
  // überspringen. Workflow: Klick auf "Demo bauen" → Modal → Speichern oder Skip.
  const [demoOverrides, setDemoOverrides] = useState({
    slogan: "",
    top_services: ["", "", ""],
    client_quote: "",
    client_quote_author: "",
    logo_url: "",
  });
  const [showPreDemoModal, setShowPreDemoModal] = useState(false);
  const [savingDemoOverrides, setSavingDemoOverrides] = useState(false);
  const navigate = useNavigate();

  const reload = async () => {
    if (!leadId) return;
    const [l, a, t] = await Promise.all([
      axios.get(`${API}/leads/${leadId}`, { withCredentials: true }),
      axios.get(`${API}/leads/${leadId}/activities`, { withCredentials: true }),
      axios.get(`${API}/leads/${leadId}/tasks`, { withCredentials: true }),
    ]);
    setLead(l.data);
    setActivities(a.data);
    setTasks(t.data);
    // Demo-Overrides aus Lead-Daten initialisieren (für Pre-Build-Modal)
    const ov = l.data?.demo_overrides || {};
    setDemoOverrides({
      slogan: ov.slogan || "",
      top_services: Array.isArray(ov.top_services) && ov.top_services.length ? [...ov.top_services, "", "", ""].slice(0, 3) : ["", "", ""],
      client_quote: ov.client_quote || "",
      client_quote_author: ov.client_quote_author || "",
      logo_url: ov.logo_url || "",
    });
  };

  useEffect(() => { if (open && leadId) reload(); /* eslint-disable-next-line */ }, [leadId, open]);

  // Tag-Vorschläge: alle bisher verwendeten Tags im System (für Auto-Complete)
  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/leads/tags`, { withCredentials: true })
      .then(r => setAllTags((r.data || []).map(t => typeof t === "string" ? t : t.tag || t.name || "").filter(Boolean)))
      .catch(() => {});
  }, [open]);

  const saveTags = async (newTags) => {
    try {
      await axios.put(`${API}/leads/${leadId}`, { tags: newTags }, { withCredentials: true });
      setLead(l => ({ ...l, tags: newTags }));
      onUpdate?.();
    } catch (err) { toast.error(err.response?.data?.detail || "Tag-Update fehlgeschlagen"); }
  };

  const removeTag = (tag) => saveTags((lead.tags || []).filter(t => t !== tag));

  const addTag = (tag) => {
    const clean = (tag || "").trim().toLowerCase();
    if (!clean) return;
    if ((lead.tags || []).includes(clean)) { toast.info("Tag existiert bereits"); return; }
    saveTags([...(lead.tags || []), clean]);
    setTagInput("");
  };

  // Reset edit mode when modal closes/lead changes
  useEffect(() => { setEditing(false); }, [leadId, open]);

  const startEdit = () => {
    setEditForm({
      company_name: lead.company_name || "",
      contact_name: lead.contact_name || "",
      contact_email: lead.contact_email || "",
      contact_phone: lead.contact_phone || "",
      address: lead.address || "",
      city: lead.city || "",
      website: lead.website || "",
      google_maps_url: lead.google_maps_url || "",
    });
    setEditing(true);
  };

  const cancelEdit = () => { setEditing(false); setEditForm({}); };

  const saveEdit = async () => {
    if (!editForm.company_name?.trim()) {
      toast.error("Firmenname ist Pflicht");
      return;
    }
    setSavingEdit(true);
    try {
      await axios.put(`${API}/leads/${leadId}`, editForm, { withCredentials: true });
      toast.success("Lead aktualisiert");
      setEditing(false);
      await reload();
      onUpdate?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally {
      setSavingEdit(false);
    }
  };

  const isAdmin = currentUser?.role === "admin";
  const owner = users.find(u => u.id === lead?.assigned_to);
  const stage = stages.find(s => s.id === lead?.stage_id);

  const handleStageMove = async (stageId) => {
    // Nur noch Won/Lost werden manuell gesetzt (Backend lehnt andere ab für non-admins)
    const newStage = stages.find(s => s.id === stageId);
    if (newStage?.is_lost) {
      setPendingLostStage(stageId);
      return;
    }
    try {
      await axios.put(`${API}/leads/${leadId}/stage`, { stage_id: stageId, lost_reason: null }, { withCredentials: true });
      toast.success(`Stage: ${newStage?.name}`);
      await reload();
      onUpdate?.();
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
  };

  const handleMarkWon = async () => {
    const wonStage = stages.find(s => s.is_won);
    if (!wonStage) { toast.error("Keine Won-Stage konfiguriert"); return; }
    if (!window.confirm(`Lead "${lead.company_name}" als GEWONNEN markieren?`)) return;
    await handleStageMove(wonStage.id);
  };

  const handleMarkLost = () => {
    const lostStage = stages.find(s => s.is_lost);
    if (!lostStage) { toast.error("Keine Lost-Stage konfiguriert"); return; }
    setPendingLostStage(lostStage.id);
  };

  const handleMarkOfferSent = async () => {
    if (!window.confirm("Markieren: Angebot wurde versendet?\nLoggt eine Activity (Stage bleibt unverändert).")) return;
    try {
      await axios.post(`${API}/leads/${leadId}/mark-offer-sent`, {}, { withCredentials: true });
      toast.success("Activity geloggt: Angebot versendet");
      await reload();
      onUpdate?.();
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
  };

  const handleReopenLead = async () => {
    // Lead von Won/Lost zurück auf vorherige offene Stage öffnen.
    // Wir wählen automatisch die letzte nicht-terminale Stage (höchste Order ohne is_won/is_lost),
    // typischerweise "Demo geschickt" oder "Terminiert".
    const openStages = stages.filter(s => !s.is_won && !s.is_lost).sort((a, b) => (b.order || 0) - (a.order || 0));
    if (openStages.length === 0) { toast.error("Keine offene Stage konfiguriert"); return; }
    const target = openStages[0];
    if (!window.confirm(`Lead "${lead.company_name}" wieder öffnen?\n\nStage wird zurückgesetzt auf: ${target.name}`)) return;
    try {
      await axios.put(`${API}/leads/${leadId}/stage`, { stage_id: target.id, lost_reason: null }, { withCredentials: true });
      toast.success(`Lead wieder offen: ${target.name}`);
      await reload();
      onUpdate?.();
    } catch (err) { toast.error(err.response?.data?.detail || "Lead konnte nicht wieder geöffnet werden"); }
  };

  const handleLostReasonConfirmed = async (reason) => {
    if (!pendingLostStage) return;
    try {
      await axios.put(`${API}/leads/${leadId}/stage`, { stage_id: pendingLostStage, lost_reason: reason }, { withCredentials: true });
      toast.success("Als verloren markiert");
      setPendingLostStage(null);
      await reload();
      onUpdate?.();
    } catch (err) { toast.error("Fehler"); setPendingLostStage(null); }
  };

  const handleAssign = async (userId) => {
    try {
      await axios.put(`${API}/leads/${leadId}/assign`, { assigned_to: userId === "_unassign" ? null : userId }, { withCredentials: true });
      toast.success("Owner aktualisiert");
      await reload();
      onUpdate?.();
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler — nur Admins"); }
  };

  const handleAddActivity = async (e) => {
    e.preventDefault();
    if (!actForm.body && !actForm.title) { toast.error("Inhalt fehlt"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/leads/${leadId}/activities`, { type: actType, ...actForm }, { withCredentials: true });
      toast.success("Eintrag gespeichert");
      setActForm({ title: "", body: "", outcome: "erreicht", duration_min: 0 });
      await reload();
    } catch (err) { toast.error("Fehler"); } finally { setSaving(false); }
  };

  const handleAddTask = async (e) => {
    e.preventDefault();
    if (!taskForm.title) return;
    setSaving(true);
    try {
      await axios.post(`${API}/leads/${leadId}/tasks`, taskForm, { withCredentials: true });
      toast.success("Aufgabe angelegt");
      setTaskForm({ title: "", type: "call", due_at: "", notes: "" });
      await reload();
    } catch (err) { toast.error("Fehler"); } finally { setSaving(false); }
  };

  const handleToggleTask = async (task) => {
    try {
      await axios.put(`${API}/tasks/${task.id}`, { completed: !task.completed }, { withCredentials: true });
      await reload();
    } catch (err) { toast.error("Fehler"); }
  };

  const handleDeleteTask = async (taskId) => {
    if (!window.confirm("Aufgabe löschen?")) return;
    await axios.delete(`${API}/tasks/${taskId}`, { withCredentials: true });
    await reload();
  };

  const handleDeleteActivity = async (actId) => {
    if (!window.confirm("Eintrag löschen?")) return;
    try {
      await axios.delete(`${API}/leads/${leadId}/activities/${actId}`, { withCredentials: true });
      await reload();
    } catch (err) { toast.error(err.response?.data?.detail || "Nicht erlaubt"); }
  };

  const [demoBuilding, setDemoBuilding] = useState(false);
  const [variantsBuilding, setVariantsBuilding] = useState(false);
  const [variantsData, setVariantsData] = useState(null); // { variants: [...], compare_token, customer_feedback }
  const [variantsPolling, setVariantsPolling] = useState(false);
  const [tracking, setTracking] = useState(null); // { total_views, by_variant, recent_events, ... }

  const fetchTracking = async () => {
    try {
      const r = await axios.get(`${API}/leads/${leadId}/tracking`, { withCredentials: true });
      setTracking(r.data);
    } catch (err) {
      // Schweige bei 404 — Lead hat einfach noch keinen Traffic
    }
  };

  const fetchVariants = async () => {
    try {
      const r = await axios.get(`${API}/leads/${leadId}/variants`, { withCredentials: true });
      setVariantsData(r.data);
      const anyPending = (r.data.variants || []).some(v => v.pending);
      setVariantsPolling(anyPending);
      // Tracking parallel mit nachladen
      fetchTracking();
    } catch (err) {
      // Schweige bei 404 — Lead hat einfach noch keine Varianten
    }
  };

  useEffect(() => {
    if (leadId && open) fetchVariants();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadId, open]);

  // Polling während Varianten generiert werden
  useEffect(() => {
    if (!variantsPolling) return;
    const id = setInterval(fetchVariants, 5000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variantsPolling]);

  const handleGenerateVariants = async () => {
    if (!window.confirm(`4 KI-generierte Konzept-Varianten für "${lead.company_name}" erstellen?\n\n✓ 4 verschiedene Templates × Brand-DNAs\n✓ Alle laufen parallel im Hintergrund (~30-60s)\n✓ Eine öffentliche Compare-URL für deinen Kunden\n✓ "Gefällt mir"-Feedback wird im Lead protokolliert`)) return;
    setVariantsBuilding(true);
    try {
      const res = await axios.post(`${API}/leads/${leadId}/generate-variants`, {}, { withCredentials: true, timeout: 30000 });
      if (res.data.already_exists) {
        toast.success("Varianten existieren bereits");
      } else {
        toast.success("4 Varianten werden generiert (~30-60s) – Vorschau aktualisiert sich automatisch");
      }
      setVariantsData({ variants: res.data.variants, compare_token: res.data.compare_token, compare_slug: res.data.compare_slug || "", customer_feedback: [] });
      setVariantsPolling(true);
      onUpdate?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler beim Generieren der Varianten");
    } finally {
      setVariantsBuilding(false);
    }
  };

  const handleCopyCompareLink = () => {
    const slugOrToken = variantsData?.compare_slug || variantsData?.compare_token;
    if (!slugOrToken) return;
    // Slug → kurzer Vanity-Pfad /k/, Token → klassisch /compare/
    const path = variantsData?.compare_slug ? `/k/${slugOrToken}` : `/compare/${slugOrToken}`;
    const url = `${window.location.origin}${path}`;
    navigator.clipboard.writeText(url);
    toast.success("Vergleichs-Link kopiert");
  };

  const handleConvertToDemo = async () => {
    // Wenn eine Website hinterlegt ist, überspringen wir das Pre-Build-Modal
    // komplett und starten direkt den Scrape+KI-Entwurf — die Website liefert
    // eh deutlich bessere Daten als die vier optionalen Overrides.
    // User-Feedback: Modal soll NUR erscheinen, wenn keine Website gepflegt
    // ist (z.B. Google-Place-Lead ohne Online-Präsenz).
    const hasWebsite = Boolean(
      (lead?.website && lead.website.trim()) ||
      (lead?.company_website && lead.company_website.trim())
    );
    if (hasWebsite) {
      await runConvertToDemo();
      return;
    }
    // Sonst: Modal öffnen, das den Vertriebler einlädt 4 Felder zu ergänzen
    // (oder zu überspringen). Das Modal ersetzt das hässliche window.confirm().
    setShowPreDemoModal(true);
  };

  // Tatsächlicher Demo-Build — wird vom Pre-Build-Modal aus aufgerufen, entweder
  // direkt (Skip) oder nachdem die Overrides gespeichert wurden.
  // 2-Schritt-Workflow (2026-05-02): Wir rufen jetzt /prepare-demo statt
  // /convert-to-demo. Das scrapt nur + legt leeres Project an. Der Vertriebler
  // wählt im Builder Template + KI-Anweisungen + Subpages und triggert
  // „Neu erstellen" manuell. Vorteile: User-Kontrolle vor LLM-Kosten,
  // Subpage-Auswahl basierend auf gecrawlten Daten, individuelle Brand-DNA.
  const runConvertToDemo = async () => {
    setShowPreDemoModal(false);
    setDemoBuilding(true);
    try {
      const res = await axios.post(`${API}/leads/${leadId}/prepare-demo`, {}, { withCredentials: true, timeout: 90000 });
      let msg;
      if (res.data.already_exists) {
        msg = res.data.auto_generate ? "Demo wird fertig gebaut → Builder geöffnet" : "Demo existiert bereits → Builder geöffnet";
      } else {
        const scrapedHint = res.data.scraped_ok
          ? ` (${res.data.scraped_subpages_count || 0} Subseiten gefunden)`
          : "";
        msg = `Daten gesammelt${scrapedHint} → Website wird jetzt automatisch gebaut`;
      }
      toast.success(msg);
      onUpdate?.();
      onClose();
      navigate(res.data.builder_url);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler beim Vorbereiten der Demo");
    } finally {
      setDemoBuilding(false);
    }
  };

  const handleOpenDemo = () => {
    if (!lead.demo?.builder_url) return;
    onClose();
    navigate(lead.demo.builder_url);
  };

  // Speichert die 4 Pre-Build-Modal-Felder und startet danach den Demo-Build.
  // Wenn alle Felder leer sind, überspringen wir den Save-Roundtrip (gleicher
  // Effekt wie der „Überspringen"-Button).
  const handleSaveDemoOverridesAndBuild = async () => {
    const cleanServices = demoOverrides.top_services.map(s => (s || "").trim()).filter(Boolean);
    const hasAny = (
      demoOverrides.slogan.trim() ||
      cleanServices.length > 0 ||
      demoOverrides.client_quote.trim() ||
      demoOverrides.logo_url.trim()
    );
    if (!hasAny) {
      // Nichts gefüllt → wie Skip behandeln
      runConvertToDemo();
      return;
    }
    setSavingDemoOverrides(true);
    try {
      await axios.put(`${API}/leads/${leadId}`, {
        demo_overrides: {
          slogan: demoOverrides.slogan.trim(),
          top_services: cleanServices,
          client_quote: demoOverrides.client_quote.trim(),
          client_quote_author: demoOverrides.client_quote_author.trim(),
          logo_url: demoOverrides.logo_url.trim(),
        }
      }, { withCredentials: true });
      toast.success("Demo-Daten gespeichert · Build startet...");
      runConvertToDemo();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler beim Speichern");
      setSavingDemoOverrides(false);
    } finally {
      setSavingDemoOverrides(false);
    }
  };

  const handleCopyDemoLink = async () => {
    if (!lead.demo?.preview_url) {
      toast.error("Kein öffentlicher Demo-Link verfügbar");
      return;
    }
    const fullUrl = `${window.location.origin}${lead.demo.preview_url}`;
    try {
      await navigator.clipboard.writeText(fullUrl);
      toast.success("Demo-Link kopiert");
    } catch (e) {
      toast.error("Kopieren fehlgeschlagen");
    }
  };

  const handleLogMeeting = () => setShowAppointment(true);

  if (!lead) {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-3xl rounded-sm">
          <DialogTitle className="sr-only">Lead-Details werden geladen</DialogTitle>
          <DialogDescription className="sr-only">Bitte warten — Lead-Daten werden gerade geladen.</DialogDescription>
          <p className="text-sm text-muted-foreground p-6">Lade...</p>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl rounded-sm max-h-[92vh] overflow-y-auto p-0" data-testid="lead-detail-modal">
        <DialogTitle className="sr-only">{lead?.name || "Lead-Details"}</DialogTitle>
        <DialogDescription className="sr-only">Detail-Ansicht und Bearbeitung des Leads inkl. Kontaktdaten, Notizen, Pipeline-Stage und Demo-Erstellung.</DialogDescription>
        {/* Header */}
        <div className="px-6 py-5 border-b border-border bg-muted/20">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <p className="text-xs uppercase tracking-widest text-muted-foreground mb-0.5">Lead · {lead.source}</p>
              {!editing ? (
                <>
                  <div className="flex items-center gap-2 group min-w-0">
                    <h2 className="font-heading text-xl font-black tracking-tight truncate min-w-0">{lead.company_name}</h2>
                    <button
                      onClick={startEdit}
                      data-testid="edit-lead-btn"
                      title="Lead bearbeiten"
                      className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded-sm text-muted-foreground hover:text-foreground hover:bg-muted"
                    >
                      <Pencil size={13} />
                    </button>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">
                    {lead.contact_name || "—"}
                    {lead.contact_email && <> · <a href={`mailto:${lead.contact_email}`} className="hover:underline">{lead.contact_email}</a></>}
                    {lead.contact_phone && <> · <a href={`tel:${lead.contact_phone}`} className="hover:underline font-mono">{lead.contact_phone}</a></>}
                  </p>
                  {(lead.address || lead.city || lead.website || lead.google_maps_url) && (
                    <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-3 flex-wrap">
                      {(lead.address || lead.city) && <span className="inline-flex items-center gap-1"><MapPin size={11} /> {[lead.address, lead.city].filter(Boolean).join(", ")}</span>}
                      {lead.website && <a href={lead.website.startsWith("http") ? lead.website : `https://${lead.website}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:underline" data-testid="lead-website-link"><Globe size={11} /> {lead.website.replace(/^https?:\/\//, "").replace(/\/$/, "")}</a>}
                      {lead.google_maps_url && <a href={lead.google_maps_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:underline text-emerald-700 dark:text-emerald-400" data-testid="lead-gmaps-link" title="Auf Google Maps öffnen"><MapPin size={11} /> Google Maps</a>}
                    </p>
                  )}
                  {/* Tags — klickbar editierbar */}
                  <div className="mt-1.5">
                    {!editingTags ? (
                      <div className="flex gap-1 flex-wrap items-center">
                        {(lead.tags || []).map(t => (
                          <span key={t} className="text-[10px] uppercase tracking-widest font-bold bg-muted text-muted-foreground px-1.5 py-0.5 rounded inline-flex items-center gap-1" data-testid={`tag-${t}`}>{t}</span>
                        ))}
                        <button
                          type="button"
                          onClick={() => setEditingTags(true)}
                          className="text-[10px] uppercase tracking-widest font-medium text-muted-foreground hover:text-foreground border border-dashed border-muted-foreground/30 hover:border-foreground/50 px-1.5 py-0.5 rounded inline-flex items-center gap-1 transition-colors"
                          data-testid="edit-tags-btn"
                          title="Branchen-Tags bearbeiten"
                        >
                          <Pencil size={9} /> {(lead.tags || []).length === 0 ? "Tags hinzufügen" : "Bearbeiten"}
                        </button>
                      </div>
                    ) : (
                      <div className="bg-muted/40 border border-border rounded-sm p-2 space-y-2" data-testid="tags-editor">
                        <div className="flex flex-wrap gap-1 items-center">
                          {(lead.tags || []).map(t => (
                            <span key={t} className="text-[10px] uppercase tracking-widest font-bold bg-foreground text-background px-1.5 py-0.5 rounded inline-flex items-center gap-1">
                              {t}
                              <button type="button" onClick={() => removeTag(t)} className="hover:opacity-70" data-testid={`tag-remove-${t}`}><X size={9} /></button>
                            </span>
                          ))}
                          <Input
                            value={tagInput}
                            onChange={e => setTagInput(e.target.value)}
                            onKeyDown={e => {
                              if (e.key === "Enter") { e.preventDefault(); addTag(tagInput); }
                              else if (e.key === "Escape") setEditingTags(false);
                            }}
                            placeholder="Tag eingeben + Enter"
                            className="h-6 text-[11px] rounded-sm w-40 border-dashed"
                            data-testid="tag-input"
                            autoFocus
                          />
                          <button type="button" onClick={() => setEditingTags(false)} className="text-[10px] text-muted-foreground hover:text-foreground ml-auto" data-testid="tags-done-btn">Fertig</button>
                        </div>
                        {/* Vorschläge: bisher genutzte Tags */}
                        {allTags.filter(t => !(lead.tags || []).includes(t)).length > 0 && (
                          <div>
                            <p className="text-[9px] uppercase tracking-widest font-semibold text-muted-foreground mb-1">Vorhandene Tags · klick zum Hinzufügen</p>
                            <div className="flex flex-wrap gap-1">
                              {allTags
                                .filter(t => !(lead.tags || []).includes(t))
                                .filter(t => !tagInput || t.toLowerCase().includes(tagInput.toLowerCase()))
                                .slice(0, 24)
                                .map(t => (
                                  <button
                                    key={t}
                                    type="button"
                                    onClick={() => addTag(t)}
                                    className="text-[10px] uppercase tracking-widest font-medium px-1.5 py-0.5 rounded border border-border hover:bg-foreground hover:text-background hover:border-foreground transition-colors"
                                    data-testid={`tag-suggest-${t}`}
                                  >
                                    + {t}
                                  </button>
                                ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div className="space-y-2 mt-1" data-testid="edit-lead-form">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <div>
                      <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Firmenname *</Label>
                      <Input
                        value={editForm.company_name}
                        onChange={e => setEditForm({ ...editForm, company_name: e.target.value })}
                        className="h-8 text-sm rounded-sm mt-0.5"
                        data-testid="edit-company-name"
                        autoFocus
                      />
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Ansprechpartner</Label>
                      <Input
                        value={editForm.contact_name}
                        onChange={e => setEditForm({ ...editForm, contact_name: e.target.value })}
                        className="h-8 text-sm rounded-sm mt-0.5"
                        data-testid="edit-contact-name"
                      />
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">E-Mail</Label>
                      <Input
                        type="email"
                        value={editForm.contact_email}
                        onChange={e => setEditForm({ ...editForm, contact_email: e.target.value })}
                        className="h-8 text-sm rounded-sm mt-0.5 font-mono"
                        data-testid="edit-contact-email"
                        placeholder="kontakt@firma.de"
                      />
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Telefon</Label>
                      <Input
                        type="tel"
                        value={editForm.contact_phone}
                        onChange={e => setEditForm({ ...editForm, contact_phone: e.target.value })}
                        className="h-8 text-sm rounded-sm mt-0.5 font-mono"
                        data-testid="edit-contact-phone"
                        placeholder="+49 163 1234567"
                      />
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Adresse</Label>
                      <Input
                        value={editForm.address}
                        onChange={e => setEditForm({ ...editForm, address: e.target.value })}
                        className="h-8 text-sm rounded-sm mt-0.5"
                        data-testid="edit-address"
                      />
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Ort</Label>
                      <Input
                        value={editForm.city}
                        onChange={e => setEditForm({ ...editForm, city: e.target.value })}
                        className="h-8 text-sm rounded-sm mt-0.5"
                        data-testid="edit-city"
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Website</Label>
                      <Input
                        value={editForm.website}
                        onChange={e => setEditForm({ ...editForm, website: e.target.value })}
                        className="h-8 text-sm rounded-sm mt-0.5 font-mono"
                        data-testid="edit-website"
                        placeholder="https://..."
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Google Maps URL</Label>
                      <Input
                        value={editForm.google_maps_url}
                        onChange={e => setEditForm({ ...editForm, google_maps_url: e.target.value })}
                        className="h-8 text-sm rounded-sm mt-0.5 font-mono"
                        data-testid="edit-google-maps-url"
                        placeholder="https://www.google.com/maps/place/..."
                      />
                    </div>
                  </div>
                  <div className="flex gap-2 pt-1">
                    <Button size="sm" onClick={saveEdit} disabled={savingEdit} className="rounded-sm gap-1.5 h-8" data-testid="save-edit-btn">
                      {savingEdit ? <RefreshCw size={13} className="animate-spin" /> : <Save size={13} />}
                      Speichern
                    </Button>
                    <Button size="sm" variant="outline" onClick={cancelEdit} className="rounded-sm h-8" data-testid="cancel-edit-btn">
                      Abbrechen
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
          {/* Stage (read-only, auto-getriggert) + Owner row */}
          <div className="flex flex-wrap gap-2 mt-4 items-center">
            <div
              className="inline-flex items-center gap-1.5 h-8 px-3 rounded-sm border bg-card text-xs font-bold uppercase tracking-wider"
              style={{ borderColor: stage?.color, color: stage?.color }}
              title="Stage wird automatisch durch Aktionen ausgelöst"
              data-testid="stage-readonly-badge"
            >
              <span className="w-2 h-2 rounded-full" style={{ background: stage?.color || "#94a3b8" }} />
              {stage?.name || "—"}
            </div>
            <Select value={lead.assigned_to || "_unassign"} onValueChange={handleAssign} disabled={!isAdmin}>
              <SelectTrigger className="w-48 rounded-sm h-8 text-xs" data-testid="owner-picker">
                <SelectValue placeholder="Nicht zugewiesen" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="_unassign">— Nicht zugewiesen —</SelectItem>
                {users.map(u => <SelectItem key={u.id} value={u.id}>{u.name || u.email} <span className="text-muted-foreground ml-1">· {u.role}</span></SelectItem>)}
              </SelectContent>
            </Select>
            {!isAdmin && <span className="text-xs text-muted-foreground self-center">(nur Admins können zuweisen)</span>}
            {(stage?.is_won || stage?.is_lost) && (
              <span
                className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-sm ${stage?.is_won ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400" : "bg-red-500/10 text-red-600 dark:text-red-400"}`}
                data-testid="closed-stage-indicator"
                title="Lead ist abgeschlossen — kann jederzeit wieder geöffnet werden"
              >
                {stage?.is_won ? <CheckCircle2 size={11} /> : <X size={11} />}
                {stage?.is_won ? "Gewonnen" : "Verloren"}
              </span>
            )}
            <div className="flex gap-1.5 ml-auto flex-wrap">
              {/* Aktionen — IMMER verfügbar (auch bei Gewonnen/Verloren) */}
              <Button size="sm" variant="outline" onClick={() => setShowCallTracker(true)} className="rounded-sm gap-1.5 h-8 border-green-500/30 text-green-500 hover:bg-green-500/10" data-testid="call-lead-btn" title={lead.contact_phone || "Anruf starten"}>
                <PhoneCall size={13} /> Anrufen
              </Button>
              <Button size="sm" variant="outline" onClick={() => setShowSendEmail(true)} disabled={!lead.contact_email} className="rounded-sm gap-1.5 h-8" data-testid="send-email-btn" title={lead.contact_email ? "" : "Lead hat keine E-Mail"}>
                <Mail size={13} /> E-Mail
              </Button>
              {!stage?.is_won && !stage?.is_lost && (
                <Button size="sm" variant="outline" onClick={handleLogMeeting} className="rounded-sm gap-1.5 h-8 border-cyan-500/30 text-cyan-600 hover:bg-cyan-500/10 dark:text-cyan-400" data-testid="log-meeting-btn" title="Termin buchen · setzt Stage auf Terminiert">
                  <CalendarPlus size={13} /> Termin buchen
                </Button>
              )}
              {lead.demo?.builder_url ? (
                <>
                  <Button size="sm" variant="outline" onClick={handleOpenDemo} className="rounded-sm gap-1.5 h-8 border-amber-500/30 text-amber-600 hover:bg-amber-500/10 dark:text-amber-400" data-testid="open-demo-btn" title="Demo im Builder öffnen">
                    <Eye size={13} /> Demo öffnen
                  </Button>
                  {lead.demo.preview_url && (
                    <Button size="sm" variant="outline" onClick={handleCopyDemoLink} className="rounded-sm gap-1.5 h-8" data-testid="copy-demo-link-btn" title="Öffentlichen Demo-Link kopieren">
                      <Copy size={13} /> Demo-Link
                    </Button>
                  )}
                </>
              ) : (
                <Button size="sm" variant="outline" onClick={handleConvertToDemo} disabled={demoBuilding} className="rounded-sm gap-1.5 h-8" data-testid="convert-demo-btn">
                  {demoBuilding ? <><RefreshCw size={13} className="animate-spin" /> Analysiere...</> : <><Sparkles size={13} /> Demo bauen</>}
                </Button>
              )}
              <Button
                size="sm"
                variant="outline"
                onClick={handleGenerateVariants}
                disabled={variantsBuilding || (variantsData?.variants?.length >= 4)}
                className="rounded-sm gap-1.5 h-8 border-violet-300 text-violet-700 hover:bg-violet-50 dark:border-violet-900 dark:text-violet-400 dark:hover:bg-violet-950/30"
                data-testid="generate-variants-btn"
                title="4 Konzept-Varianten parallel generieren — perfekt für Kunden-Präsentationen"
              >
                {variantsBuilding ? <><RefreshCw size={13} className="animate-spin" /> Starte...</> : <><Sparkles size={13} /> 4 Varianten</>}
              </Button>
              {!stage?.is_won && !stage?.is_lost ? (
                <>
                  <Button size="sm" variant="outline" onClick={handleMarkWon} className="rounded-sm gap-1.5 h-8 border-emerald-300 text-emerald-700 hover:bg-emerald-50 dark:border-emerald-900 dark:text-emerald-400 dark:hover:bg-emerald-950/30" data-testid="mark-won-btn">
                    <CheckCircle2 size={13} /> Gewonnen
                  </Button>
                  <Button size="sm" variant="outline" onClick={handleMarkLost} className="rounded-sm gap-1.5 h-8 border-red-300 text-red-700 hover:bg-red-50 dark:border-red-900 dark:text-red-400 dark:hover:bg-red-950/30" data-testid="mark-lost-btn">
                    <X size={13} /> Verloren
                  </Button>
                </>
              ) : (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleReopenLead}
                  className="rounded-sm gap-1.5 h-8 border-blue-400 text-blue-700 hover:bg-blue-50 dark:border-blue-700 dark:text-blue-300 dark:hover:bg-blue-950/30"
                  data-testid="reopen-lead-btn"
                  title="Lead wieder öffnen — z. B. wenn versehentlich auf Gewonnen/Verloren gesetzt"
                >
                  <RefreshCw size={13} /> Lead wieder öffnen
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Variants Section — sichtbar wenn Multi-Demo gestartet wurde */}
        {variantsData && variantsData.variants && variantsData.variants.length > 0 && (
          <>
            {/* Demo-Tracker — sichtbar sobald der Kunde den Compare-Link geöffnet hat */}
            {tracking && tracking.total_views > 0 && (
              <div className="border-t border-border bg-amber-50/40 dark:bg-amber-950/10 px-6 py-4" data-testid="tracking-section">
                <div className="flex items-center gap-2 mb-3 flex-wrap">
                  <Activity size={14} className="text-amber-600" />
                  <h4 className="text-sm font-bold uppercase tracking-wider">Kundenaktivität</h4>
                </div>
                {/* KPI-Reihe */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
                  <div className="bg-card border border-border rounded-sm px-3 py-2" data-testid="kpi-total-views">
                    <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">Aufrufe gesamt</div>
                    <div className="text-2xl font-bold">{tracking.total_views}</div>
                  </div>
                  <div className="bg-card border border-border rounded-sm px-3 py-2" data-testid="kpi-unique-visitors">
                    <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">Unique Besucher</div>
                    <div className="text-2xl font-bold">{tracking.unique_visitors}</div>
                  </div>
                  <div className="bg-card border border-border rounded-sm px-3 py-2" data-testid="kpi-variant-views">
                    <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">Varianten geöffnet</div>
                    <div className="text-2xl font-bold">{tracking.variant_views}</div>
                  </div>
                  <div className="bg-card border border-border rounded-sm px-3 py-2" data-testid="kpi-last-seen">
                    <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">Letzter Besuch</div>
                    <div className="text-base font-bold leading-tight">{formatRelativeMinutes(tracking.minutes_since_last_view)}</div>
                  </div>
                </div>
                {/* Variant-Heatmap */}
                {tracking.by_variant && tracking.by_variant.length > 0 && (
                  <div className="mb-3" data-testid="variant-heatmap">
                    <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground mb-1.5">Klickt am häufigsten</div>
                    <div className="flex flex-wrap gap-1.5">
                      {tracking.by_variant.slice(0, 4).map((v, i) => (
                        <span
                          key={v.project_id}
                          className={`text-xs px-2 py-1 rounded-sm border flex items-center gap-1.5 ${i === 0 ? "border-amber-400 bg-amber-100/60 dark:bg-amber-950/30 dark:border-amber-700 font-bold" : "border-border bg-muted/40"}`}
                          data-testid={`variant-heat-${i}`}
                        >
                          <span>{v.label || "—"}</span>
                          <span className="text-[10px] tabular-nums opacity-70">{v.views}×</span>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {/* Timeline */}
                {tracking.recent_events && tracking.recent_events.length > 0 && (
                  <details className="group" data-testid="tracking-timeline">
                    <summary className="cursor-pointer text-xs text-muted-foreground hover:text-foreground select-none flex items-center gap-1">
                      <span className="group-open:rotate-90 transition-transform inline-block">▸</span>
                      Letzte {tracking.recent_events.length} Aktivitäten
                    </summary>
                    <ul className="mt-2 space-y-1 pl-3 border-l-2 border-amber-200 dark:border-amber-900/40">
                      {tracking.recent_events.map((e, i) => (
                        <li key={i} className="text-xs flex items-start gap-2 leading-snug">
                          {e.event_type === "compare_viewed" && <Eye size={11} className="mt-0.5 text-violet-500 flex-shrink-0" />}
                          {e.event_type === "variant_viewed" && <ExternalLink size={11} className="mt-0.5 text-amber-500 flex-shrink-0" />}
                          {e.event_type === "variant_liked" && <Heart size={11} className="mt-0.5 text-rose-500 flex-shrink-0" />}
                          <span className="text-muted-foreground">
                            {e.event_type === "compare_viewed" && "Compare-Page geöffnet"}
                            {e.event_type === "variant_viewed" && `Variante geöffnet: `}
                            {e.event_type === "variant_liked" && `Variante gewählt: `}
                          </span>
                          {e.variant_label && <span className="font-medium">{e.variant_label}</span>}
                          <span className="ml-auto text-muted-foreground tabular-nums whitespace-nowrap">{formatRelativeIso(e.created_at)}</span>
                        </li>
                      ))}
                    </ul>
                  </details>
                )}
              </div>
            )}
          <div className="border-t border-border bg-violet-50/40 dark:bg-violet-950/10 px-6 py-4" data-testid="variants-section">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <Sparkles size={14} className="text-violet-600" />
                <h4 className="text-sm font-bold uppercase tracking-wider">Konzept-Varianten ({variantsData.variants.length})</h4>
                {variantsPolling && (
                  <span className="text-xs text-violet-600 dark:text-violet-400 flex items-center gap-1">
                    <RefreshCw size={11} className="animate-spin" /> Generiert…
                  </span>
                )}
              </div>
              {variantsData.compare_token && (
                <Button size="sm" variant="outline" onClick={handleCopyCompareLink} className="rounded-sm gap-1.5 h-7 text-xs" data-testid="copy-compare-link-btn">
                  <Copy size={11} /> Vergleichs-Link kopieren
                </Button>
              )}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {variantsData.variants.map((v, idx) => {
                const fb = (variantsData.customer_feedback || []).find(f => f.project_id === v.project_id);
                return (
                  <div
                    key={v.project_id}
                    className={`bg-card border rounded-sm p-3 transition-all ${fb ? "border-emerald-400 ring-2 ring-emerald-200 dark:ring-emerald-900/40" : "border-border"}`}
                    data-testid={`variant-card-${idx}`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">{v.template_id.replace("-", " ")}</div>
                      {fb && <span className="text-[9px] font-bold text-emerald-700 bg-emerald-100 dark:bg-emerald-950/40 dark:text-emerald-400 px-1.5 py-0.5 rounded-sm uppercase">★ Kunde</span>}
                      {v.pending && <RefreshCw size={11} className="animate-spin text-violet-500" />}
                    </div>
                    <div className="font-bold text-sm leading-tight mb-2">{v.label}</div>
                    <div className="text-xs text-muted-foreground leading-snug mb-2 line-clamp-2">{v.mood}</div>
                    <div className="flex flex-wrap gap-1">
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={v.pending}
                        onClick={() => navigate(v.builder_url)}
                        className="rounded-sm gap-1 h-6 px-2 text-[10px] flex-1"
                        data-testid={`variant-builder-${idx}`}
                      >
                        <Pencil size={9} /> Bearbeiten
                      </Button>
                      {v.preview_url && !v.pending && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => window.open(v.preview_url, "_blank")}
                          className="rounded-sm gap-1 h-6 px-2 text-[10px]"
                          title="Live-Vorschau öffnen"
                          data-testid={`variant-preview-${idx}`}
                        >
                          <ExternalLink size={9} />
                        </Button>
                      )}
                    </div>
                    {fb && fb.comment && (
                      <div className="text-[10px] mt-2 p-1.5 bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-200 dark:border-emerald-900 rounded-sm text-emerald-900 dark:text-emerald-300 italic">
                        "{fb.comment}"
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
          </>
        )}

        {/* Tabs */}
        <div className="border-b border-border px-6 flex gap-1 bg-card">
          {[
            { k: "timeline", l: `Verlauf (${activities.length})` },
            { k: "tasks", l: `Aufgaben (${tasks.filter(t => !t.completed).length})` },
            { k: "deal", l: "Deal-Daten" },
          ].map(t => (
            <button
              key={t.k}
              onClick={() => setTab(t.k)}
              className={`px-4 py-3 text-xs font-semibold uppercase tracking-widest border-b-2 transition-colors ${tab === t.k ? "border-foreground text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}
              data-testid={`tab-${t.k}`}
            >
              {t.l}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {tab === "timeline" && (
            <div className="space-y-5">
              {/* Add activity */}
              <div className="bg-muted/30 border border-border rounded-sm p-4">
                <div className="flex gap-1 mb-3">
                  {["call", "note", "email", "meeting"].map(t => {
                    const M = ACTIVITY_TYPES[t];
                    return (
                      <button key={t} onClick={() => setActType(t)}
                        className={`px-3 py-1.5 text-xs font-semibold rounded-sm flex items-center gap-1.5 transition-colors ${actType === t ? "bg-foreground text-background" : "bg-card border border-border text-muted-foreground hover:text-foreground"}`}
                        data-testid={`act-type-${t}`}
                      >
                        <M.icon size={12} /> {M.label}
                      </button>
                    );
                  })}
                </div>
                <form onSubmit={handleAddActivity} className="space-y-2.5" data-testid="add-activity-form">
                  {actType === "call" && (
                    <div className="grid grid-cols-2 gap-2">
                      <Select value={actForm.outcome} onValueChange={v => setActForm(f => ({ ...f, outcome: v }))}>
                        <SelectTrigger className="rounded-sm h-9 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>{CALL_OUTCOMES.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                      </Select>
                      <Input type="number" min="0" placeholder="Dauer (Min.)" value={actForm.duration_min || ""} onChange={e => setActForm(f => ({ ...f, duration_min: parseInt(e.target.value, 10) || 0 }))} className="rounded-sm h-9 text-xs" />
                    </div>
                  )}
                  <Textarea rows={3} placeholder={actType === "call" ? "Was wurde besprochen?" : actType === "note" ? "Deine Notiz..." : actType === "email" ? "Betreff oder Inhalt..." : "Termin-Details..."}
                    value={actForm.body} onChange={e => setActForm(f => ({ ...f, body: e.target.value }))} className="rounded-sm text-sm" data-testid="activity-body" />
                  <div className="flex justify-end">
                    <Button type="submit" size="sm" disabled={saving} className="rounded-sm" data-testid="save-activity-btn">{saving ? "..." : "Eintrag speichern"}</Button>
                  </div>
                </form>
              </div>
              {/* Timeline */}
              <div className="space-y-3">
                {activities.length === 0 && <p className="text-sm text-muted-foreground text-center py-6">Noch kein Verlauf — leg los mit dem ersten Anruf.</p>}
                {activities.map(a => {
                  const M = ACTIVITY_TYPES[a.type] || ACTIVITY_TYPES.note;
                  const Icon = M.icon;
                  const canDelete = !["stage_change", "assign", "system"].includes(a.type);
                  return (
                    <div key={a.id} className="flex gap-3 group" data-testid={`activity-${a.id}`}>
                      <div className={`w-7 h-7 rounded-full bg-card border border-border flex items-center justify-center shrink-0 ${M.color}`}>
                        <Icon size={13} />
                      </div>
                      <div className="flex-1 min-w-0 bg-card border border-border rounded-sm p-3">
                        <div className="flex items-center justify-between gap-2 mb-1">
                          <div className="flex items-center gap-2 text-xs">
                            <span className="font-semibold text-foreground">{M.label}</span>
                            {a.outcome && <span className="px-1.5 py-0.5 bg-muted rounded text-[10px] uppercase font-bold tracking-wider">{CALL_OUTCOMES.find(o => o.value === a.outcome)?.label || a.outcome}</span>}
                            {a.duration_min ? <span className="text-muted-foreground">· {a.duration_min} Min</span> : null}
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-muted-foreground">{fmtDate(a.created_at)} · {a.created_by_name}</span>
                            {canDelete && (
                              <button onClick={() => handleDeleteActivity(a.id)} className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive">
                                <Trash2 size={12} />
                              </button>
                            )}
                          </div>
                        </div>
                        {a.title && <p className="text-sm font-medium text-foreground">{a.title}</p>}
                        {a.body && <p className="text-sm text-muted-foreground whitespace-pre-wrap mt-0.5">{a.body}</p>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {tab === "tasks" && (
            <div className="space-y-4">
              <form onSubmit={handleAddTask} className="bg-muted/30 border border-border rounded-sm p-4 space-y-2.5" data-testid="add-task-form">
                <Input placeholder="Was steht an? z.B. Demo bauen + Termin nächste Woche" value={taskForm.title} onChange={e => setTaskForm(f => ({ ...f, title: e.target.value }))} className="rounded-sm" data-testid="task-title-input" required />
                <div className="grid grid-cols-2 gap-2">
                  <Select value={taskForm.type} onValueChange={v => setTaskForm(f => ({ ...f, type: v }))}>
                    <SelectTrigger className="rounded-sm h-9 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="call">Anruf</SelectItem>
                      <SelectItem value="email">E-Mail schreiben</SelectItem>
                      <SelectItem value="meeting">Termin</SelectItem>
                      <SelectItem value="follow_up">Follow-up</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input type="datetime-local" value={taskForm.due_at} onChange={e => setTaskForm(f => ({ ...f, due_at: e.target.value }))} className="rounded-sm h-9 text-xs" data-testid="task-due-input" />
                </div>
                <Button type="submit" size="sm" disabled={saving} className="rounded-sm" data-testid="save-task-btn">Aufgabe anlegen</Button>
              </form>
              {tasks.length === 0 && <p className="text-sm text-muted-foreground text-center py-6">Keine Aufgaben — alles erledigt!</p>}
              {tasks.map(t => (
                <div key={t.id} className={`flex items-start gap-3 p-3 border rounded-sm group ${t.completed ? "border-border bg-muted/20" : "border-border bg-card"}`} data-testid={`task-${t.id}`}>
                  <button onClick={() => handleToggleTask(t)} className="mt-0.5 shrink-0" data-testid={`toggle-task-${t.id}`}>
                    {t.completed ? <CheckCircle2 size={18} className="text-emerald-600" /> : <Circle size={18} className="text-muted-foreground" />}
                  </button>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium ${t.completed ? "line-through text-muted-foreground" : "text-foreground"}`}>{t.title}</p>
                    <div className="flex flex-wrap gap-x-3 text-xs text-muted-foreground mt-0.5">
                      <span>{t.type}</span>
                      {t.due_at && <span>📅 {fmtDate(t.due_at)}</span>}
                      {t.completed && t.completed_at && <span className="text-emerald-600">✓ {fmtDate(t.completed_at)}</span>}
                    </div>
                  </div>
                  <button onClick={() => handleDeleteTask(t.id)} className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive">
                    <Trash2 size={13} />
                  </button>
                </div>
              ))}
            </div>
          )}

          {tab === "deal" && (
            <div className="space-y-4 max-w-xl">
              {/* Score Box */}
              {lead.score != null && (
                <ScoreBreakdownPanel
                  lead={lead}
                  onRefresh={async () => {
                    try {
                      const r = await axios.post(`${API}/leads/${leadId}/refresh-score?with_website_check=true`, {}, { withCredentials: true });
                      toast.success(`Score aktualisiert: ${r.data.score}/100 (${r.data.category})`);
                      reload();
                    } catch { toast.error("Fehler beim Score-Update"); }
                  }}
                />
              )}
              {lead.message && (
                <div className="pt-3 border-t border-border">
                  <Label className="text-xs uppercase tracking-wide text-muted-foreground">Ursprüngliche Anfrage</Label>
                  <p className="text-sm text-muted-foreground whitespace-pre-wrap mt-1">{lead.message}</p>
                </div>
              )}
            </div>
          )}
        </div>
        <LostReasonModal
          open={!!pendingLostStage}
          onClose={() => setPendingLostStage(null)}
          onConfirm={handleLostReasonConfirmed}
        />
        <SendEmailModal
          open={showSendEmail}
          onClose={() => setShowSendEmail(false)}
          leadId={leadId}
          leadEmail={lead?.contact_email}
          onSent={reload}
        />
        <CallTrackerDialog
          open={showCallTracker}
          onClose={() => setShowCallTracker(false)}
          lead={lead}
          onCallLogged={reload}
        />
        <AppointmentDialog
          open={showAppointment}
          onClose={() => setShowAppointment(false)}
          lead={lead}
          onCreated={() => { reload(); onUpdate?.(); }}
        />

        {/* ── Pre-Build-Modal: öffnet vor convert-to-demo. Vertriebler kann
              4 kritische Felder ergänzen oder skip drücken. Bei Leads ohne
              Website wird ein Empfehlungs-Banner oben angezeigt. ──── */}
        <Dialog open={showPreDemoModal} onOpenChange={(o) => !o && !demoBuilding && !savingDemoOverrides && setShowPreDemoModal(false)}>
          <DialogContent className="max-w-xl rounded-sm" data-testid="pre-demo-modal">
            <DialogTitle className="sr-only">Bevor wir die Demo bauen — Optionale Felder</DialogTitle>
            <DialogDescription className="sr-only">Ergänze 4 optionale Felder (Slogan, USPs, Bilder-Stichworte, Tonalität) damit die KI eine echtere Demo erstellt. Du kannst auch direkt überspringen.</DialogDescription>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-sm bg-gradient-to-br from-amber-400 to-orange-500 grid place-items-center flex-shrink-0">
                  <Sparkles size={18} className="text-white" />
                </div>
                <div className="min-w-0">
                  <h2 className="text-lg font-bold leading-tight">Bevor wir die Demo bauen…</h2>
                  <p className="text-sm text-muted-foreground mt-0.5">
                    Vier optionale Felder, die deine Demo deutlich echter machen.
                    {!lead?.website && <span className="block mt-1 text-amber-700 dark:text-amber-400 font-semibold">⚠ Keine Website hinterlegt — wir empfehlen die Felder auszufüllen.</span>}
                  </p>
                </div>
              </div>

              <div className="space-y-2.5 max-h-[55vh] overflow-y-auto pr-1">
                <div>
                  <Label htmlFor="pre-do-slogan" className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">Slogan / Hero-Untertitel</Label>
                  <Input
                    id="pre-do-slogan"
                    value={demoOverrides.slogan}
                    onChange={e => setDemoOverrides(o => ({ ...o, slogan: e.target.value }))}
                    placeholder='z.B. "Maler-Meister mit Herz – seit 1985 in Duisburg"'
                    className="mt-1 rounded-sm h-9 text-sm"
                    data-testid="pre-do-slogan"
                    maxLength={140}
                  />
                </div>

                <div>
                  <Label className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">Top-3 Leistungen / Schwerpunkte</Label>
                  <div className="mt-1 space-y-1.5">
                    {[0, 1, 2].map(i => (
                      <Input
                        key={i}
                        value={demoOverrides.top_services[i] || ""}
                        onChange={e => setDemoOverrides(o => {
                          const arr = [...(o.top_services || ["", "", ""])];
                          arr[i] = e.target.value;
                          return { ...o, top_services: arr };
                        })}
                        placeholder={["Innenraum-Gestaltung", "Fassadenanstrich", "Vorher/Nachher Renovierung"][i]}
                        className="rounded-sm h-9 text-sm"
                        data-testid={`pre-do-service-${i}`}
                        maxLength={60}
                      />
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                  <div className="md:col-span-2">
                    <Label htmlFor="pre-do-quote" className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">Echte Kundenquote (optional)</Label>
                    <Textarea
                      id="pre-do-quote"
                      value={demoOverrides.client_quote}
                      onChange={e => setDemoOverrides(o => ({ ...o, client_quote: e.target.value }))}
                      placeholder='z.B. „Sauber, pünktlich, fair – gerne wieder."'
                      className="mt-1 rounded-sm text-sm min-h-[64px]"
                      data-testid="pre-do-quote"
                      maxLength={240}
                    />
                  </div>
                  <div>
                    <Label htmlFor="pre-do-quote-author" className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">Verfasser/in</Label>
                    <Input
                      id="pre-do-quote-author"
                      value={demoOverrides.client_quote_author}
                      onChange={e => setDemoOverrides(o => ({ ...o, client_quote_author: e.target.value }))}
                      placeholder="z.B. Frau Schulz"
                      className="mt-1 rounded-sm h-9 text-sm"
                      data-testid="pre-do-quote-author"
                      maxLength={60}
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="pre-do-logo" className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">Logo-URL (optional)</Label>
                  <Input
                    id="pre-do-logo"
                    value={demoOverrides.logo_url}
                    onChange={e => setDemoOverrides(o => ({ ...o, logo_url: e.target.value }))}
                    placeholder="https://… (oder leer lassen für Initialen-Logo)"
                    className="mt-1 rounded-sm h-9 text-sm"
                    data-testid="pre-do-logo"
                  />
                </div>
              </div>

              <div className="flex items-center justify-between gap-2 pt-2 border-t border-border">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={runConvertToDemo}
                  disabled={demoBuilding || savingDemoOverrides}
                  className="rounded-sm h-9 text-sm"
                  data-testid="pre-demo-skip"
                >
                  Überspringen
                </Button>
                <Button
                  size="sm"
                  onClick={handleSaveDemoOverridesAndBuild}
                  disabled={demoBuilding || savingDemoOverrides}
                  className="rounded-sm gap-1.5 h-9 text-sm bg-foreground text-background hover:bg-foreground/90"
                  data-testid="pre-demo-save-build"
                >
                  {(demoBuilding || savingDemoOverrides) ? (
                    <><RefreshCw size={14} className="animate-spin" /> Demo wird gebaut…</>
                  ) : (
                    <><Sparkles size={14} /> Speichern & Demo bauen</>
                  )}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
}
